import { useState } from "react";
import { toast } from "sonner";
import { ThumbsUp } from "@phosphor-icons/react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Public like button — distinct from the private Watchlist heart.
 *
 * Props:
 *   listingId       — required
 *   initialLiked    — optional (default false); the parent should have hydrated this
 *   initialCount    — optional (default 0)
 *   size            — "sm" | "md" (default "sm")
 *   className       — extra classes for the wrapper button
 *
 * Optimistic UI: flips instantly, rolls back on server error.
 */
export default function LikeButton({
  listingId,
  initialLiked = false,
  initialCount = 0,
  size = "sm",
  className = "",
}) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!initialLiked);
  const [count, setCount] = useState(Number(initialCount) || 0);
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user) {
      toast.error("Sign in to like cards");
      return;
    }
    if (busy) return;
    const prevLiked = liked;
    const prevCount = count;
    // Optimistic flip
    setLiked(!prevLiked);
    setCount(prevCount + (prevLiked ? -1 : 1));
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/like`);
      setLiked(!!data.liked);
      setCount(Number(data.likes_count) || 0);
    } catch (err) {
      setLiked(prevLiked);
      setCount(prevCount);
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const isSm = size === "sm";
  const iconSize = isSm ? 12 : 16;
  const pad = isSm ? "px-1.5 py-0.5" : "px-2.5 py-1.5";
  const text = isSm ? "text-[10px]" : "text-xs";

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`like-btn-${listingId}`}
      aria-pressed={liked}
      title={liked ? "You liked this card" : "Like this card"}
      className={`inline-flex items-center gap-1 font-mono tracking-widest uppercase border transition-colors ${pad} ${text} ${
        liked
          ? "border-[#FF5F1F] text-[#FF5F1F] bg-[#FF5F1F]/10"
          : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
      } ${className}`}
    >
      <ThumbsUp size={iconSize} weight={liked ? "fill" : "bold"} />
      <span data-testid={`like-count-${listingId}`} className="tabular-nums font-bold">{count}</span>
    </button>
  );
}
